/** @jsxImportSource @emotion/react */
import { TimeSlot, UserDetails } from '@/types/calendar'
import { TOKENS } from '@gen/tokenConstants'
import { useDesignTokens } from '@hooks/useDesignTokens'
import React, { useState } from 'react'

interface BookingModalProps {
  timeSlot: TimeSlot
  onClose: () => void
  onConfirm: (userDetails: UserDetails) => void
}

export default function BookingModal({
  timeSlot,
  onClose,
  onConfirm,
}: BookingModalProps) {
  const { getValue, getClasses } = useDesignTokens()
  const [userDetails, setUserDetails] = useState<UserDetails>({
    name: '',
    notes: '',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (userDetails.name) {
      onConfirm(userDetails)
    }
  }

  function formatTime(date: Date) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  return (
    <div
      css={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: getValue(TOKENS.spacing.md),
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        css={{
          backgroundColor: getValue(TOKENS.color.neutral[50]),
          borderRadius: getValue(TOKENS.borderRadius.lg),
          padding: getValue(TOKENS.spacing.md),
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div css={{ marginBottom: getValue(TOKENS.spacing.lg) }}>
          <h2
            css={{
              fontSize: getValue(TOKENS.typography.fontSize.h3),
              fontWeight: getValue(TOKENS.typography.fontWeight.semibold),
              color: getValue(TOKENS.color.neutral[900]),
              margin: 0,
              marginBottom: getValue(TOKENS.spacing.sm),
            }}
          >
            Book It
          </h2>
          <p
            css={{
              fontSize: getValue(TOKENS.typography.fontSize.md),
              color: getValue(TOKENS.color.neutral[600]),
              margin: 0,
            }}
          >
            {formatTime(timeSlot.start)} - {formatTime(timeSlot.end)}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div css={{ marginBottom: getValue(TOKENS.spacing.md) }}>
            <label
              css={{
                display: 'block',
                fontSize: getValue(TOKENS.typography.fontSize.sm),
                fontWeight: getValue(TOKENS.typography.fontWeight.medium),
                color: getValue(TOKENS.color.neutral[700]),
                marginBottom: getValue(TOKENS.spacing.xs),
              }}
            >
              Title *
            </label>
            <input
              type='text'
              required
              value={userDetails.name}
              onChange={(e) =>
                setUserDetails({ ...userDetails, name: e.target.value })
              }
              css={{
                width: '100%',
                padding: getValue(TOKENS.spacing.md),
                fontSize: getValue(TOKENS.typography.fontSize.md),
                border: `1px solid ${getValue(TOKENS.color.neutral[300])}`,
                borderRadius: getValue(TOKENS.borderRadius.md),
                fontFamily: getValue(TOKENS.typography.fontFamily.primary),
                '&:focus': {
                  outline: 'none',
                  borderColor: getValue(TOKENS.color.primary[500]),
                  boxShadow: `0 0 0 3px ${getValue(TOKENS.color.primary[100])}`,
                },
              }}
            />
          </div>

          <div css={{ marginBottom: getValue(TOKENS.spacing.lg) }}>
            <label
              css={{
                display: 'block',
                fontSize: getValue(TOKENS.typography.fontSize.sm),
                fontWeight: getValue(TOKENS.typography.fontWeight.medium),
                color: getValue(TOKENS.color.neutral[700]),
                marginBottom: getValue(TOKENS.spacing.xs),
              }}
            >
              Notes (optional)
            </label>
            <textarea
              rows={3}
              value={userDetails.notes}
              onChange={(e) =>
                setUserDetails({ ...userDetails, notes: e.target.value })
              }
              css={{
                width: '100%',
                padding: getValue(TOKENS.spacing.md),
                fontSize: getValue(TOKENS.typography.fontSize.md),
                border: `1px solid ${getValue(TOKENS.color.neutral[300])}`,
                borderRadius: getValue(TOKENS.borderRadius.md),
                fontFamily: getValue(TOKENS.typography.fontFamily.primary),
                resize: 'vertical',
                minHeight: '80px',
                '&:focus': {
                  outline: 'none',
                  borderColor: getValue(TOKENS.color.primary[500]),
                  boxShadow: `0 0 0 3px ${getValue(TOKENS.color.primary[100])}`,
                },
              }}
            />
          </div>

          <div
            css={{
              display: 'flex',
              gap: getValue(TOKENS.spacing.md),
              justifyContent: 'flex-end',
            }}
          >
            <button
              type='button'
              onClick={onClose}
              css={{
                ...getClasses(TOKENS.layout.button.base),
                padding: {
                  mobile: getValue(TOKENS.spacing.sm),
                  tablet: getValue(TOKENS.spacing.md),
                },
                fontSize: getValue(TOKENS.typography.fontSize.sm),
                fontWeight: getValue(TOKENS.typography.fontWeight.medium),
                backgroundColor: 'transparent',
                color: getValue(TOKENS.color.neutral[600]),
                border: `1px solid ${getValue(TOKENS.color.neutral[300])}`,
                borderRadius: getValue(TOKENS.borderRadius.md),
                '&:hover': {
                  backgroundColor: getValue(TOKENS.color.neutral[100]),
                },
              }}
            >
              Cancel
            </button>
            <button
              type='submit'
              css={{
                ...getClasses(TOKENS.layout.button.base),
                padding: {
                  mobile: getValue(TOKENS.spacing.sm),
                  tablet: getValue(TOKENS.spacing.md),
                },
                fontSize: getValue(TOKENS.typography.fontSize.sm),
                fontWeight: getValue(TOKENS.typography.fontWeight.medium),
                backgroundColor: getValue(TOKENS.color.primary[500]),
                color: getValue(TOKENS.color.neutral[50]),
                border: 'none',
                borderRadius: getValue(TOKENS.borderRadius.md),
                '&:hover': {
                  backgroundColor: getValue(TOKENS.color.primary[600]),
                },
                '&:disabled': {
                  backgroundColor: getValue(TOKENS.color.neutral[400]),
                  cursor: 'not-allowed',
                },
              }}
              disabled={!userDetails.name}
            >
              Confirm Booking
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
